import React, { FC } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone';
import {
  Box,
  Avatar,
  Text,
  Divider,
  TextButton,
  TextProps,
} from '@wix/design-system';
import { User, Email, Public, AdminAccess } from '@wix/wix-ui-icons-common';
import { useTemplateContext } from '@components/Contexts/TemplateContext';
import { profilePath } from '@components/Hooks/useRouter';
import AdditionalInfo from '../AdditionalInfo';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import s from './CreatorBox.module.scss';

type CreatorBoxProps = {
  isSticky: boolean;
  showAdditionalInfo?: boolean;
  textSize?: TextProps['size'];
  isMobileView: boolean;
};

const CreatorBox: FC<CreatorBoxProps> = ({
  isSticky,
  showAdditionalInfo,
  textSize = 'medium',
  isMobileView,
}) => {
  const { t } = useTranslation();
  const { template } = useTemplateContext();
  const { logBiClick } = useTemplateBi({ is_mobile: isMobileView });
  const {
    partnerProfileDetails: {
      slug,
      email,
      profileImage,
      businessName,
      businessWebsite,
      creatorIsActiveInMarketplace,
    },
  } = template;
  return (
    <Box
      dataHook="creator-details-box"
      position="sticky"
      top={isSticky ? '138px' : 'auto'}
      direction="vertical"
    >
      <Box
        padding="24px"
        borderRadius="8px"
        direction="vertical"
        background="#f7f8f9"
      >
        <Box direction="horizontal" gap="17px">
          <Avatar
            imgProps={{
              alt: businessName,
              className: s.avatar,
              src: profileImage,
              width: '48px',
              height: '48px',
            }}
          />
          <Box direction="vertical" marginBottom="24px">
            <Text size={textSize} secondary>
              {t('marketplace.template-page.creator-info-title')}
            </Text>
            <Text
              secondary
              size="medium"
              weight="bold"
              tagName="h2"
              className={s.businessName}
            >
              {businessName}
            </Text>
          </Box>
        </Box>
        <Box direction="vertical" gap="12px">
          <TextButton
            dataHook={`creator-cta-${
              creatorIsActiveInMarketplace ? 'partner' : 'agency'
            }`}
            skin="dark"
            onClick={() =>
              logBiClick({
                clickType: creatorIsActiveInMarketplace
                  ? 'view profile'
                  : 'visit website',
              })
            }
            as="a"
            target="_blank"
            href={
              creatorIsActiveInMarketplace
                ? `${profilePath}/${slug}`
                : businessWebsite
            }
          >
            {creatorIsActiveInMarketplace ? (
              <Box verticalAlign="middle" gap="6px">
                <User aria-hidden="true" />
                <Text
                  size={textSize}
                  dataHook="marketplace.template-page.creator-profile-cta"
                >
                  {t('marketplace.template-page.creator-profile-cta')}
                </Text>
              </Box>
            ) : (
              <Box verticalAlign="middle" gap="6px">
                <Public />
                <Text
                  size={textSize}
                  dataHook="marketplace.template-page.creator-agency-website-cta"
                >
                  {t('marketplace.template-page.creator-agency-website-cta')}
                </Text>
              </Box>
            )}
          </TextButton>
          <TextButton
            dataHook="creator-email-cta"
            skin="dark"
            as="a"
            target="_blank"
            href={`mailto:${email}`}
            onClick={() => {
              logBiClick({ clickType: 'Contact seller' });
            }}
          >
            <Box verticalAlign="middle" gap="6px">
              <Email aria-hidden="true" />
              <Text size={textSize}>
                {t('marketplace.template-page.creator-email-cta')}
              </Text>
            </Box>
          </TextButton>
          {template.privacyPolicyUrl && (
            <TextButton
              dataHook="creator-privacy-policy-cta"
              skin="dark"
              as="a"
              target="_blank"
              href={template.privacyPolicyUrl}
              onClick={() => {
                logBiClick({ clickType: 'Privacy Policy' });
              }}
            >
              <Box verticalAlign="middle" gap="6px">
                <AdminAccess aria-hidden="true" />
                <Text size={textSize}>
                  {t('marketplace.template-page.creator-privacy-policy-cta')}
                </Text>
              </Box>
            </TextButton>
          )}
        </Box>
        <Box padding="24px 0">
          <Divider />
        </Box>
        <Text size="small" secondary>
          {t('marketplace.template-page.support-description')}
        </Text>
      </Box>
      {showAdditionalInfo && (
        <Box paddingTop="24px">
          <AdditionalInfo isMobileView={false} />
        </Box>
      )}
    </Box>
  );
};

export default CreatorBox;
